import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Loader2, ArrowRight } from "lucide-react";

const EMPTY = {
  nome: "", codice_fiscale: "", piva: "", indirizzo: "", cap: "", citta: "", provincia: "",
};

export default function BillingDialog({ open, onClose, onConfirm, loading, productName, checkoutUrl }) {
  const [billing, setBilling] = useState(EMPTY);

  const set = (k) => (e) => setBilling({ ...billing, [k]: e.target.value });

  const confirm = () => {
    onConfirm(billing);
  };

  useEffect(() => {
    if (checkoutUrl) {
      const t = setTimeout(() => {
        try { window.top.location.href = checkoutUrl; } catch (e) { window.location.href = checkoutUrl; }
      }, 600);
      return () => clearTimeout(t);
    }
  }, [checkoutUrl]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/80 px-4 backdrop-blur-sm"
          data-testid="billing-dialog"
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto border border-line bg-surface p-8"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="label-caps text-mist">{checkoutUrl ? "Tutto pronto" : "Quasi fatto"}</p>
                <h3 className="mt-2 font-display text-2xl text-ivory">{productName}</h3>
              </div>
              <button onClick={onClose} data-testid="billing-close" className="text-mute transition-colors duration-300 hover:text-ivory">
                <X size={20} />
              </button>
            </div>

            {checkoutUrl ? (
              <div className="mt-8" data-testid="billing-goto-block">
                <p className="text-sm leading-relaxed text-mute">
                  Il tuo pagamento sicuro è pronto. Se non si apre automaticamente, clicca qui sotto.
                </p>
                <a
                  href={checkoutUrl}
                  target="_top"
                  rel="noopener"
                  data-testid="billing-goto-stripe"
                  className="btn-primary mt-6 w-full"
                >
                  Vai al pagamento sicuro <ArrowRight size={14} />
                </a>
                <p className="mt-4 text-center text-xs text-mute">Pagamento sicuro con Stripe — carta, Apple Pay e altri metodi.</p>
              </div>
            ) : (
              <>
                <p className="mt-8 text-sm leading-relaxed text-mute">
                  Prima del pagamento mi servono i tuoi dati di fatturazione: la fattura è obbligatoria
                  per legge e la emetto per ogni acquisto.
                </p>
                <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="billing-fields">
                  <div className="sm:col-span-2">
                    <label className="label-caps mb-2 block text-ivory/60">Nome e cognome / Ragione sociale *</label>
                    <input className="input-dark" data-testid="billing-nome" value={billing.nome} onChange={set("nome")} />
                  </div>
                  <div>
                    <label className="label-caps mb-2 block text-ivory/60">Codice fiscale *</label>
                    <input className="input-dark" data-testid="billing-cf" value={billing.codice_fiscale} onChange={set("codice_fiscale")} />
                  </div>
                  <div>
                    <label className="label-caps mb-2 block text-ivory/60">P.IVA (se presente)</label>
                    <input className="input-dark" data-testid="billing-piva" value={billing.piva} onChange={set("piva")} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="label-caps mb-2 block text-ivory/60">Indirizzo *</label>
                    <input className="input-dark" data-testid="billing-indirizzo" value={billing.indirizzo} onChange={set("indirizzo")} placeholder="Via, numero civico" />
                  </div>
                  <div>
                    <label className="label-caps mb-2 block text-ivory/60">CAP *</label>
                    <input className="input-dark" data-testid="billing-cap" value={billing.cap} onChange={set("cap")} />
                  </div>
                  <div>
                    <label className="label-caps mb-2 block text-ivory/60">Città *</label>
                    <input className="input-dark" data-testid="billing-citta" value={billing.citta} onChange={set("citta")} />
                  </div>
                  <div>
                    <label className="label-caps mb-2 block text-ivory/60">Provincia *</label>
                    <input className="input-dark" data-testid="billing-provincia" value={billing.provincia} onChange={set("provincia")} placeholder="Es. MI" maxLength={2} />
                  </div>
                </div>

                <button
                  onClick={confirm}
                  disabled={loading || !billing.nome || !billing.codice_fiscale || !billing.indirizzo || !billing.cap || !billing.citta || !billing.provincia}
                  data-testid="billing-confirm"
                  className="btn-primary mt-8 w-full disabled:opacity-50"
                >
                  {loading ? <Loader2 size={14} className="animate-spin" /> : "Procedi al pagamento"}
                </button>
                <p className="mt-4 text-center text-xs text-mute">Pagamento sicuro con Stripe — carta, Apple Pay e altri metodi.</p>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
